import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InventoryIcon from '@mui/icons-material/Inventory';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import PinDropIcon from '@mui/icons-material/PinDrop';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  CircularProgress,
  Paper,
  Tooltip as MuiTooltip,
  Typography,
  type TooltipProps,
} from '@mui/material';
import { useMemo, useRef, useState, type ReactElement } from 'react';
import {
  useProductPerformance,
  type ProductMomentum,
  type ProductPerformanceItem,
  type DateRange,
} from '../api/depletions';
import { useClickOutside } from '../hooks/useClickOutside';
import { fmt6PkNumber } from '../lib/volume';
import { colors } from '../theme';
import {
  excelSheet,
  isoDate,
  num as toNum,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';
import { DepletionsTrendChart } from './DepletionsTrendChart';
import { PremisesBadge } from './PremisesBadge';
import { SectionExplainer } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';

// Premium light-theme tooltip — white surface, hairline border, soft
// shadow, arrow, quicker enterDelay. Exported as `Tooltip` (shadowing
// the MUI import) so every `<Tooltip>` call site in this file picks
// up the styling automatically.
interface RichTooltipProps {
  title: TooltipProps['title'];
  children: ReactElement;
  placement?: TooltipProps['placement'];
  maxWidth?: number;
}

function Tooltip({ title, children, placement = 'top', maxWidth = 320 }: RichTooltipProps) {
  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow
      enterDelay={120}
      enterNextDelay={50}
      leaveDelay={40}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.05)',
            fontSize: 11.5,
            lineHeight: 1.45,
            maxWidth,
            padding: '8px 10px',
            fontFeatureSettings: '"tnum","lnum"',
          },
        },
        arrow: {
          sx: {
            color: '#fff',
            '&::before': {
              border: `1px solid ${colors.border}`,
              backgroundColor: '#fff',
            },
          },
        },
      }}
    >
      {children}
    </MuiTooltip>
  );
}

interface Props {
  range?: DateRange;
}

const num = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
const numInt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

// Momentum tier → presentation. Keep in sync with the backend
// classifier `_classify_product_momentum` in depletions_strategic.py.
const MOMENTUM_PRESENTATION: Record<
  ProductMomentum,
  { label: string; color: string; bg: string; Icon: typeof TrendingUpIcon }
> = {
  rising: {
    label: 'Rising',
    color: colors.success,
    bg: `${colors.success}14`,
    Icon: TrendingUpIcon,
  },
  steady: { label: 'Steady', color: colors.textSecondary, bg: '#f1f5f9', Icon: TrendingFlatIcon },
  slipping: {
    label: 'Slipping',
    color: colors.warning,
    bg: `${colors.warning}14`,
    Icon: TrendingDownIcon,
  },
  declining: {
    label: 'Declining',
    color: colors.error,
    bg: `${colors.error}14`,
    Icon: TrendingDownIcon,
  },
  new: {
    label: 'New',
    color: colors.chartPrimary,
    bg: `${colors.chartPrimary}14`,
    Icon: TrendingUpIcon,
  },
};

type SortKey = 'volume' | 'momentum' | 'accounts' | 'depth';
type FilterKey = 'all' | 'rising' | 'slipping_or_worse';

const SORT_OPTIONS: { key: SortKey; label: string; describe: string }[] = [
  { key: 'volume', label: 'Volume', describe: 'Total 6-pk, largest first' },
  { key: 'momentum', label: 'Momentum', describe: 'QoQ % change, biggest gain first' },
  { key: 'accounts', label: 'Reach', describe: 'Account count, widest first' },
  { key: 'depth', label: 'Depth', describe: 'Avg 6-pk per account, deepest first' },
];

const FILTER_OPTIONS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All SKUs' },
  { key: 'rising', label: 'Rising only' },
  { key: 'slipping_or_worse', label: 'Slipping / declining' },
];

// Short month label (Jan '25) — used inside the sparkline tooltip.
function shortMonth(iso: string): string {
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[idx] ?? '?'} '${(y ?? '').slice(-2)}`;
}

// Short SKU name — strip the brand prefix so the name fits in a
// table cell. Server returns the full product name; we keep the
// display short.
function shortSkuName(full: string): string {
  for (const prefix of ['Hooten Young ', 'Hooten & Young ', 'Hooten and Young ']) {
    if (full.startsWith(prefix)) return full.slice(prefix.length).trim();
  }
  return full;
}

// Per-row 12-month bar strip — same design language the Follow-Up
// Tracker uses, so the user sees one consistent visual primitive for
// "12-month activity" across the dashboard.
//
// Design intent:
//   * Bars instead of a line — every month is individually legible
//     even at table density. A line sparkline at 1.5px stroke + 2px
//     dots was too thin and the markers were lost against the area.
//   * The PEAK month renders at full opacity in the row color; other
//     active months get a calmer tint; zero months render as a 2px
//     baseline rail so they still register as "we have data, nothing
//     moved" rather than blank space.
//   * The latest month gets a hollow ring frame on top of the bar —
//     unambiguously says "this is now" without competing for color.
//   * Per-bar tooltip shows month + 9L volume + a "Peak" badge.
//   * Tiny start/end month labels below orient the timeline.
interface SparklineProps {
  series: { period: string; cases_9l: string }[];
  color?: string;
}

const BAR_W = 9;
const BAR_GAP = 3;
const BAR_H = 30;
const ZERO_RAIL_H = 2;

function Sparkline({ series, color = colors.chartPrimary }: SparklineProps) {
  const values = series.map((p) => Number(p.cases_9l));
  const max = Math.max(...values, 0);
  const peakIdx = max > 0 ? values.indexOf(max) : -1;
  const lastIdx = series.length - 1;

  const firstLabel = series.length > 0 ? shortMonth(series[0]!.period) : '';
  const lastLabel = series.length > 0 ? shortMonth(series[series.length - 1]!.period) : '';
  const stripW = values.length * BAR_W + (values.length - 1) * BAR_GAP;

  return (
    <Box sx={{ display: 'inline-block' }}>
      {/* Bar row */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          gap: `${BAR_GAP}px`,
          height: BAR_H,
          position: 'relative',
          // Subtle horizontal baseline behind the bars — anchors the
          // strip and gives zero months a visual home.
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: colors.borderLight,
            zIndex: 0,
          },
        }}
      >
        {values.map((v, i) => {
          const isZero = v === 0;
          const h = max > 0 && v > 0 ? Math.max((v / max) * BAR_H, 4) : ZERO_RAIL_H;
          const isPeak = !isZero && i === peakIdx;
          const isLast = i === lastIdx;
          const period = series[i]?.period ?? '';
          return (
            <Tooltip
              key={period}
              title={
                <Box>
                  <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                    {shortMonth(period)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {isZero ? 'No depletions' : `${fmt6PkNumber(v)} 6-pk`}
                  </Typography>
                  {isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.success, fontWeight: 700 }}>
                      Peak month
                    </Typography>
                  )}
                  {isLast && !isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.chartPrimary, fontWeight: 700 }}>
                      Latest month
                    </Typography>
                  )}
                </Box>
              }
            >
              <Box
                sx={{
                  position: 'relative',
                  width: BAR_W,
                  height: h,
                  bgcolor: isZero ? colors.borderLight : color,
                  opacity: isZero ? 1 : isPeak ? 0.95 : 0.45,
                  borderRadius: '2px 2px 0 0',
                  transition: 'opacity 0.15s, transform 0.15s',
                  cursor: 'pointer',
                  zIndex: 1,
                  // Last-bar accent — thin hollow outline on top of
                  // the bar marks "current month" without changing
                  // its height or color saturation.
                  outline: isLast && !isZero ? `1.5px solid ${color}` : 'none',
                  outlineOffset: '-1px',
                  '&:hover': {
                    opacity: isZero ? 1 : 0.95,
                    transform: isZero ? 'none' : 'scaleY(1.08)',
                    transformOrigin: 'bottom',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Tiny month-range axis — start and end only, Stripe-style. */}
      <Box
        sx={{
          mt: 0.4,
          display: 'flex',
          justifyContent: 'space-between',
          width: stripW,
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            color: colors.textMuted,
            lineHeight: 1,
            letterSpacing: '0.04em',
            fontWeight: 500,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {firstLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: 9,
            color: colors.textMuted,
            lineHeight: 1,
            letterSpacing: '0.04em',
            fontWeight: 500,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {lastLabel}
        </Typography>
      </Box>
    </Box>
  );
}

// Threshold below which the prior-3M baseline is too small for QoQ %
// to be meaningful. Below this we show the absolute change instead.
const LOW_BASE_THRESHOLD_9L = 5;

// QoQ chip text — encapsulates the small-base logic so both the table
// and the portfolio strip read the same.
function qoqChipText(velocityPct: number | null, prior3m: number, recent3m: number): string {
  if (velocityPct === null) return 'New';
  if (prior3m < LOW_BASE_THRESHOLD_9L) {
    const diff = recent3m - prior3m;
    return `${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  }
  if (velocityPct > 999) return '+999%+';
  if (velocityPct < -999) return '−999%+';
  return `${velocityPct > 0 ? '+' : ''}${velocityPct.toFixed(0)}%`;
}

// Compact momentum chip used in the table + the portfolio strip.
// When prior_3m_9l is below the LOW_BASE threshold the percentage is
// meaningless, so we surface the absolute delta and an info dot.
interface MomentumChipProps {
  momentum: ProductMomentum;
  velocityPct: number | null;
  recent3m: number;
  prior3m: number;
  compact?: boolean;
}

function MomentumChip({
  momentum,
  velocityPct,
  recent3m,
  prior3m,
  compact = false,
}: MomentumChipProps) {
  const p = MOMENTUM_PRESENTATION[momentum];
  const lowBase = velocityPct !== null && prior3m < LOW_BASE_THRESHOLD_9L;
  const text = qoqChipText(velocityPct, prior3m, recent3m);
  return (
    <Tooltip
      placement="top"
      title={
        <Box sx={{ p: 0.5, fontSize: 11, maxWidth: 220 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
            Quarter-over-quarter
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent3m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 3M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior3m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Prior baseline is tiny ({fmt6PkNumber(prior3m)} 6-pk), so the % swing is misleading —
              showing the absolute change instead.
            </Typography>
          )}
          {velocityPct === null && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-3M activity — this SKU is brand new or restarting.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          px: compact ? 0.75 : 1,
          py: compact ? 0.2 : 0.3,
          borderRadius: 1,
          bgcolor: p.bg,
          color: p.color,
          fontWeight: 700,
          fontSize: compact ? 10.5 : 11.5,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${p.color}` : 'none',
        }}
      >
        <p.Icon sx={{ fontSize: compact ? 11 : 13 }} />
        {text}
      </Box>
    </Tooltip>
  );
}

// Year-over-year chip — sits under the QoQ chip in the table. Uses
// the same low-base detection as MomentumChip: when prior_12m is
// below LOW_BASE_THRESHOLD_9L we fall back to absolute change so a
// +75,000% number can't appear. Visually lighter than QoQ (subtler
// bg, no icon) since it's the secondary metric.
interface YoyChipProps {
  velocityPct: number | null;
  recent12m: number;
  prior12m: number;
}

function YoyChip({ velocityPct, recent12m, prior12m }: YoyChipProps) {
  // Threshold scaled for 12M comparisons — a base under 20 9L isn't
  // a meaningful baseline at the year scale.
  const LOW_BASE_12M = 20;
  const lowBase = velocityPct !== null && prior12m < LOW_BASE_12M;
  const isNew = velocityPct === null;
  const isPositive = velocityPct !== null && velocityPct > 0;
  const isNegative = velocityPct !== null && velocityPct < 0;
  const color = isNew
    ? colors.textMuted
    : lowBase
      ? colors.textSecondary
      : isPositive
        ? colors.success
        : isNegative
          ? colors.error
          : colors.textSecondary;
  const bg = isNew
    ? 'transparent'
    : lowBase
      ? '#f1f5f9'
      : isPositive
        ? `${colors.success}10`
        : isNegative
          ? `${colors.error}10`
          : '#f1f5f9';
  let text: string;
  if (isNew) {
    text = 'YoY n/a';
  } else if (lowBase) {
    const diff = recent12m - prior12m;
    text = `YoY ${diff > 0 ? '+' : ''}${fmt6PkNumber(diff)} 6-pk`;
  } else {
    const v = velocityPct!;
    const cap = v > 999 ? '+999%+' : v < -999 ? '−999%+' : `${v > 0 ? '+' : ''}${v.toFixed(0)}%`;
    text = `YoY ${cap}`;
  }
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.25, maxWidth: 240 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>Year-over-year</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
            <span style={{ opacity: 0.7 }}>Recent 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(recent12m)} 6-pk
            </span>
            <span style={{ opacity: 0.7 }}>Prior 12M</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(prior12m)} 6-pk
            </span>
          </Box>
          {lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              Prior-year baseline was tiny ({fmt6PkNumber(prior12m)} 6-pk) — showing absolute change
              instead of a misleading %.
            </Typography>
          )}
          {isNew && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              No prior-year activity. SKU is too new for a YoY read.
            </Typography>
          )}
          {!isNew && !lowBase && (
            <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
              YoY smooths the seasonal noise QoQ can pick up. When QoQ is negative but YoY is flat,
              the recent dip is likely seasonal — not a real trend.
            </Typography>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 0.65,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontWeight: 600,
          fontSize: 10,
          letterSpacing: 0.2,
          fontFeatureSettings: '"tnum"',
          whiteSpace: 'nowrap',
          cursor: 'help',
          border: lowBase ? `1px dashed ${color}` : 'none',
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// Tiny "+X / -Y (90d)" line — sits below the depth signal in the
// Reach·Depth cell so the user sees account momentum at a glance.
// Color: net positive in success, net negative in error, zero muted.
interface NetAccountsLineProps {
  gained: number;
  churned: number;
}

function NetAccountsLine({ gained, churned }: NetAccountsLineProps) {
  if (gained === 0 && churned === 0) {
    return (
      <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.2 }}>
        No acct change (90d)
      </Typography>
    );
  }
  const net = gained - churned;
  const netColor = net > 0 ? colors.success : net < 0 ? colors.error : colors.textMuted;
  return (
    <Typography
      sx={{
        fontSize: 10,
        color: colors.textMuted,
        mt: 0.2,
        fontFeatureSettings: '"tnum"',
      }}
    >
      <Box component="span" sx={{ color: colors.success, fontWeight: 600 }}>
        +{gained}
      </Box>
      {' / '}
      <Box component="span" sx={{ color: colors.error, fontWeight: 600 }}>
        −{churned}
      </Box>{' '}
      <Box component="span" sx={{ color: netColor, fontWeight: 700 }}>
        ({net > 0 ? '+' : ''}
        {net} net)
      </Box>{' '}
      90d
    </Typography>
  );
}

// Days since a YYYY-MM-DD ISO date (treated as UTC) relative to today.
// Used to render the "Dark" flag and the "Last X days ago" sub-label.
function daysSinceIso(iso: string | null): number | null {
  if (!iso) return null;
  const then = new Date(`${iso}T00:00:00Z`).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

// Health flag chip — small icon + label, dense (8.5px font) so a row
// can carry 3 without looking busy. Tooltip explains the trigger.
interface FlagChipProps {
  Icon: typeof TrendingUpIcon;
  label: string;
  color: string;
  bg: string;
  tooltip: string;
}

function FlagChip({ Icon, label, color, bg, tooltip }: FlagChipProps) {
  return (
    <Tooltip placement="top" title={tooltip}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.3,
          px: 0.55,
          py: 0.15,
          borderRadius: 0.75,
          bgcolor: bg,
          color,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          cursor: 'help',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon sx={{ fontSize: 11 }} />
        {label}
      </Box>
    </Tooltip>
  );
}

// Inline horizontal bar — used inside the expand panel to visually
// compare top accounts / top states.
interface MiniBarProps {
  share: number; // 0..1 fraction of SKU total
  color: string;
}

function MiniBar({ share, color }: MiniBarProps) {
  const pct = Math.max(0, Math.min(1, share)) * 100;
  return (
    <Box
      sx={{
        position: 'relative',
        height: 6,
        bgcolor: `${color}1a`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          bgcolor: color,
          opacity: 0.85,
          borderRadius: 1,
        }}
      />
    </Box>
  );
}

interface PortfolioTileProps {
  eyebrow: string;
  primary: string;
  primaryColor?: string;
  secondary?: React.ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'error';
  tooltip?: React.ReactNode;
}

function PortfolioTile({
  eyebrow,
  primary,
  primaryColor,
  secondary,
  tone = 'neutral',
  tooltip,
}: PortfolioTileProps) {
  const toneAccent: Record<NonNullable<PortfolioTileProps['tone']>, string> = {
    neutral: colors.chartPrimary,
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
  };
  const body = (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 1.5,
        borderRight: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderRight: 'none' },
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: tooltip ? 'help' : 'default',
        transition: 'background-color 0.12s',
        '&:hover': tooltip ? { bgcolor: '#fff' } : undefined,
      }}
    >
      <Typography
        sx={{
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: colors.textMuted,
          textTransform: 'uppercase',
        }}
      >
        {eyebrow}
      </Typography>
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 700,
          color: primaryColor ?? toneAccent[tone],
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontFeatureSettings: '"tnum","lnum"',
        }}
      >
        {primary}
      </Typography>
      {secondary !== undefined && (
        <Box sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.3 }}>{secondary}</Box>
      )}
    </Box>
  );
  if (!tooltip) return body;
  return <Tooltip title={tooltip}>{body}</Tooltip>;
}

// Structured tooltip block. Designed for column headers / portfolio
// tiles where a paragraph of prose is too dense — splits into a
// title, optional definition rows (label + description), optional
// bulleted list, and an italic "how to read it" footer.
interface TooltipBlockProps {
  title?: string;
  items?: Array<{ label: string; description: string }>;
  bullets?: string[];
  tip?: string;
  maxWidth?: number;
}

function TooltipBlock({ title, items, bullets, tip, maxWidth = 280 }: TooltipBlockProps) {
  return (
    <Box sx={{ p: 0.25, maxWidth }}>
      {title && (
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 700,
            color: colors.textPrimary,
            mb: 0.75,
            letterSpacing: '0.005em',
          }}
        >
          {title}
        </Typography>
      )}
      {items && items.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.6,
            mb: bullets || tip ? 0.75 : 0,
          }}
        >
          {items.map((it) => (
            <Box key={it.label}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1.3,
                  letterSpacing: '0.01em',
                }}
              >
                {it.label}
              </Typography>
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.45 }}>
                {it.description}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {bullets && bullets.length > 0 && (
        <Box
          component="ul"
          sx={{
            pl: 0,
            m: 0,
            mb: tip ? 0.75 : 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.3,
          }}
        >
          {bullets.map((b, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 11,
                color: colors.textSecondary,
                lineHeight: 1.4,
                position: 'relative',
                pl: 1.25,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  bgcolor: colors.textMuted,
                },
              }}
            >
              {b}
            </Box>
          ))}
        </Box>
      )}
      {tip && (
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            fontStyle: 'italic',
            lineHeight: 1.4,
            borderTop: `1px solid ${colors.borderLight}`,
            pt: 0.6,
            mt: items || bullets ? 0.5 : 0,
          }}
        >
          {tip}
        </Typography>
      )}
    </Box>
  );
}

// Sortable header cell with a built-in tooltip. Tooltip describes
// what the column means, with a dotted underline on the label so
// users can see the hover affordance.
interface ColumnHeaderProps {
  children: React.ReactNode;
  tooltip: React.ReactNode;
  align?: 'left' | 'right';
}

function ColumnHeader({ children, tooltip, align = 'left' }: ColumnHeaderProps) {
  return (
    <Box sx={{ textAlign: align }}>
      <Tooltip title={tooltip}>
        <Box
          component="span"
          sx={{
            borderBottom: `1px dotted ${colors.textMuted}80`,
            cursor: 'help',
            paddingBottom: '1px',
          }}
        >
          {children}
        </Box>
      </Tooltip>
    </Box>
  );
}

export function DepletionsProductPerformance({ range }: Props) {
  const { data, isLoading, error } = useProductPerformance(range);
  const [sort, setSort] = useState<SortKey>('volume');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);

  // Memoize the products array so its reference is stable when the
  // query result is unchanged — otherwise the downstream useMemo
  // dependencies churn every render.
  const products = useMemo(() => data?.products ?? [], [data?.products]);
  const totalAll = Number(data?.total_9l ?? 0);
  const top3Share = data?.top_3_share ?? 0;
  const referenceDate = data?.reference_date ?? null;

  // Portfolio summary — Lead / Top mover / Biggest slipper. Computed
  // from the products list; tiny enough to do client-side.
  const summary = useMemo(() => {
    if (products.length === 0) return null;
    const sortedByVolume = [...products].sort((a, b) => Number(b.cases_9l) - Number(a.cases_9l));
    const lead = sortedByVolume[0] ?? null;
    const top3 = sortedByVolume.slice(0, 3);
    const withVelocity = products.filter((p) => p.velocity_pct !== null);
    // Top Mover / Biggest Slipper both rank by absolute VOLUME change
    // quarter-over-quarter (recent 3M minus prior 3M), not by percentage —
    // a big SKU moving real cases beats a tiny SKU swinging off a small base.
    const withDelta = withVelocity.map((p) => ({
      p,
      delta: Number(p.recent_3m_9l) - Number(p.prior_3m_9l),
    }));
    const topMoverEntry = [...withDelta].sort((a, b) => b.delta - a.delta)[0];
    const topMover = topMoverEntry && topMoverEntry.delta > 0 ? topMoverEntry.p : null;
    const slipperEntry = [...withDelta].sort((a, b) => a.delta - b.delta)[0];
    const biggestSlipper = slipperEntry && slipperEntry.delta < 0 ? slipperEntry.p : null;
    return {
      lead,
      top3,
      topMover,
      topMoverDelta: topMover ? (topMoverEntry?.delta ?? 0) : 0,
      biggestSlipper,
      biggestSlipperDelta: biggestSlipper ? (slipperEntry?.delta ?? 0) : 0,
    };
  }, [products]);

  // Portfolio medians — used by per-row tooltips to surface "above /
  // below portfolio depth" context. Median over reach is also handy
  // for sanity-checking when one row dominates.
  const portfolioMedians = useMemo(() => {
    if (products.length === 0) return { depth: 0, accounts: 0 };
    const median = (vals: number[]) => {
      const sorted = vals.slice().sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      return sorted.length % 2 === 0
        ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2
        : (sorted[mid] ?? 0);
    };
    return {
      depth: median(products.map((p) => Number(p.avg_9l_per_account))),
      accounts: median(products.map((p) => p.account_count)),
    };
  }, [products]);

  // Filtered + sorted view for the table. Sort comparators keep ties
  // stable by falling back on cases_9l so the order is deterministic
  // across refreshes.
  const visible = useMemo(() => {
    let rows = products.slice();
    if (filter === 'rising') {
      rows = rows.filter((p) => p.momentum === 'rising');
    } else if (filter === 'slipping_or_worse') {
      rows = rows.filter((p) => p.momentum === 'slipping' || p.momentum === 'declining');
    }
    const tieBreak = (a: ProductPerformanceItem, b: ProductPerformanceItem) =>
      Number(b.cases_9l) - Number(a.cases_9l);
    switch (sort) {
      case 'momentum':
        rows.sort((a, b) => {
          const av = a.velocity_pct ?? -Infinity;
          const bv = b.velocity_pct ?? -Infinity;
          return bv - av || tieBreak(a, b);
        });
        break;
      case 'accounts':
        rows.sort((a, b) => b.account_count - a.account_count || tieBreak(a, b));
        break;
      case 'depth':
        rows.sort(
          (a, b) => Number(b.avg_9l_per_account) - Number(a.avg_9l_per_account) || tieBreak(a, b),
        );
        break;
      case 'volume':
      default:
        rows.sort(tieBreak);
    }
    return rows;
  }, [products, sort, filter]);

  useSectionExport(
    () =>
      buildProductExport(visible, [
        // Only a host-supplied range narrows the rows; the default view spans everything.
        range?.from || range?.to
          ? { label: 'Date range', value: `${range.from ?? 'Earliest'} to ${range.to ?? 'latest'}` }
          : { kind: 'info', label: 'Period', value: 'All available data' },
        {
          label: 'Momentum',
          value: FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All SKUs',
        },
        {
          kind: 'sort',
          label: 'Sorted by',
          value: `${SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume'} (high → low)`,
        },
        { kind: 'info', label: 'Data through', value: referenceDate ?? '' },
      ]),
    !isLoading && products.length > 0,
  );

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load product performance: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || products.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Product Performance
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Typography color="text.secondary">No products to display.</Typography>
          )}
        </Box>
      </Paper>
    );
  }

  const sortLabel = SORT_OPTIONS.find((s) => s.key === sort)?.label ?? 'Volume';
  const filterLabel = FILTER_OPTIONS.find((f) => f.key === filter)?.label ?? 'All SKUs';

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${colors.chartPrimary}14`,
          }}
        >
          <InventoryIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Product Performance
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {products.length} SKUs · {fmt6PkNumber(totalAll)} 6-pk total · Top-3 share{' '}
            {Math.round(top3Share * 100)}%{referenceDate && ` · ref ${shortMonth(referenceDate)}`} ·
            QoQ compares last 3 months to the 3 before
          </Typography>
        </Box>
      </Box>

      {/* Portfolio summary strip */}
      {summary && (
        <>
          {/* Make it explicit that every KPI here is volume-based, not %. */}
          <Typography
            sx={{ fontSize: 11, color: colors.textMuted, display: 'block', mb: 0.75, ml: 0.25 }}
          >
            All four KPIs are{' '}
            <Box component="strong" sx={{ color: colors.textSecondary }}>
              6-pk volume
            </Box>
            -based — Lead SKU by total volume, Top Mover / Biggest Slipper by volume added or lost
            quarter-over-quarter (not %), and Concentration by the top-3 SKUs&apos; share of volume.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 1.5,
              mb: 2.5,
              overflow: 'hidden',
              bgcolor: colors.surfaceSubtle,
            }}
          >
            <PortfolioTile
              eyebrow="Lead SKU"
              primary={summary.lead ? shortSkuName(summary.lead.product_name) : '—'}
              secondary={
                summary.lead && (
                  <>
                    {fmt6PkNumber(summary.lead.cases_9l)} 6-pk ·{' '}
                    <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>
                      {num.format(Number(summary.lead.cases_9l))}
                    </strong>{' '}
                    9L · {Math.round(summary.lead.pct_of_9l * 100)}% of total
                  </>
                )
              }
              tooltip={
                summary.lead ? (
                  <TooltipBlock
                    title="Volume leader"
                    items={[
                      {
                        label: summary.lead.product_name,
                        description: `${fmt6PkNumber(Number(summary.lead.cases_9l))} 6-pk · ${Math.round(summary.lead.pct_of_9l * 100)}% of total portfolio volume`,
                      },
                      {
                        label: 'Reach',
                        description: `${numInt.format(summary.lead.account_count)} accounts across ${summary.lead.state_count} states`,
                      },
                    ]}
                    tip="The SKU you'd defend first if anything else slipped."
                  />
                ) : (
                  'No products in range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Top Mover"
              primary={
                summary.topMover ? shortSkuName(summary.topMover.product_name) : 'None gaining'
              }
              tone="success"
              secondary={
                summary.topMover ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MomentumChip
                      momentum={summary.topMover.momentum}
                      velocityPct={summary.topMover.velocity_pct}
                      recent3m={Number(summary.topMover.recent_3m_9l)}
                      prior3m={Number(summary.topMover.prior_3m_9l)}
                      compact
                    />
                    <span>+{fmt6PkNumber(summary.topMoverDelta)} 6-pk QoQ</span>
                  </Box>
                ) : (
                  <span style={{ color: colors.textMuted }}>No SKU adding volume QoQ</span>
                )
              }
              tooltip={
                summary.topMover ? (
                  <TooltipBlock
                    title="Biggest volume gainer (QoQ)"
                    items={[
                      {
                        label: summary.topMover.product_name,
                        description: `Prior 3M: ${fmt6PkNumber(Number(summary.topMover.prior_3m_9l))} 6-pk → Recent 3M: ${fmt6PkNumber(Number(summary.topMover.recent_3m_9l))} 6-pk  (+${fmt6PkNumber(summary.topMoverDelta)} 6-pk added)`,
                      },
                    ]}
                    tip="Ranked by the actual 6-pk volume added, not percentage. Lean into it wherever distribution will support it."
                  />
                ) : (
                  'No SKU added volume QoQ over the selected range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Biggest Slipper"
              primary={
                summary.biggestSlipper
                  ? shortSkuName(summary.biggestSlipper.product_name)
                  : 'None slipping'
              }
              tone="error"
              secondary={
                summary.biggestSlipper ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <MomentumChip
                      momentum={summary.biggestSlipper.momentum}
                      velocityPct={summary.biggestSlipper.velocity_pct}
                      recent3m={Number(summary.biggestSlipper.recent_3m_9l)}
                      prior3m={Number(summary.biggestSlipper.prior_3m_9l)}
                      compact
                    />
                    <span>−{fmt6PkNumber(Math.abs(summary.biggestSlipperDelta))} 6-pk QoQ</span>
                  </Box>
                ) : (
                  <span style={{ color: colors.textMuted }}>No SKU losing volume QoQ</span>
                )
              }
              tooltip={
                summary.biggestSlipper ? (
                  <TooltipBlock
                    title="Biggest volume loss (QoQ)"
                    items={[
                      {
                        label: summary.biggestSlipper.product_name,
                        description: `Prior 3M: ${fmt6PkNumber(Number(summary.biggestSlipper.prior_3m_9l))} 6-pk → Recent 3M: ${fmt6PkNumber(Number(summary.biggestSlipper.recent_3m_9l))} 6-pk  (−${fmt6PkNumber(Math.abs(summary.biggestSlipperDelta))} 6-pk lost)`,
                      },
                    ]}
                    tip="Ranked by the actual 6-pk volume lost, not percentage. Investigate which accounts went quiet — open the row to see the top-3 list."
                  />
                ) : (
                  'No SKU lost volume QoQ over the selected range.'
                )
              }
            />
            <PortfolioTile
              eyebrow="Concentration"
              primary={`${Math.round(top3Share * 100)}%`}
              tone={top3Share > 0.7 ? 'warning' : 'neutral'}
              secondary={
                <>
                  Top-3 SKUs share ·{' '}
                  <strong
                    style={{ color: top3Share > 0.7 ? colors.warning : colors.textSecondary }}
                  >
                    {top3Share > 0.7 ? 'Narrow' : 'Healthy'}
                  </strong>{' '}
                  portfolio
                </>
              }
              tooltip={
                <TooltipBlock
                  title="Portfolio concentration"
                  items={[
                    {
                      label: `Top-3 share: ${Math.round(top3Share * 100)}%`,
                      description: 'Share of total 6-pk coming from the three biggest SKUs:',
                    },
                    ...summary.top3.map((p, i) => ({
                      label: `${i + 1}. ${shortSkuName(p.product_name)}`,
                      description: `${fmt6PkNumber(Number(p.cases_9l))} 6-pk · ${Math.round(p.pct_of_9l * 100)}% of total`,
                    })),
                    {
                      label: 'Threshold',
                      description:
                        'Above 70% is considered a narrow portfolio — strong leaders but a single SKU loss is hard to absorb.',
                    },
                  ]}
                />
              }
            />
          </Box>
        </>
      )}

      {/* Sort / filter toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <ToolbarMenu
          label="Sort"
          value={sortLabel}
          open={sortMenuOpen}
          onToggle={() => {
            setSortMenuOpen((o) => !o);
            setFilterMenuOpen(false);
          }}
        >
          {SORT_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={sort === opt.key}
              primary={opt.label}
              secondary={opt.describe}
              onClick={() => {
                setSort(opt.key);
                setSortMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <ToolbarMenu
          label="Filter"
          value={filterLabel}
          open={filterMenuOpen}
          onToggle={() => {
            setFilterMenuOpen((o) => !o);
            setSortMenuOpen(false);
          }}
        >
          {FILTER_OPTIONS.map((opt) => (
            <MenuRow
              key={opt.key}
              selected={filter === opt.key}
              primary={opt.label}
              onClick={() => {
                setFilter(opt.key);
                setFilterMenuOpen(false);
              }}
            />
          ))}
        </ToolbarMenu>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 11.5, color: colors.textMuted, fontFeatureSettings: '"tnum"' }}>
          {visible.length} of {products.length} SKUs
        </Typography>
      </Box>

      {/* Table */}
      <Box
        sx={{
          border: `1px solid ${colors.borderLight}`,
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '22px 32px 2fr 152px 1.3fr 1.2fr 1.2fr 1fr',
            gap: 1,
            px: 1.75,
            py: 1,
            bgcolor: colors.surfaceSubtle,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          <Box />
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="Rank"
                bullets={[
                  'Position within the current sort order',
                  'Click any row to open its deep-dive panel',
                ]}
              />
            }
          >
            #
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="Product & status flags"
                items={[
                  {
                    label: 'Tier badge',
                    description:
                      'Rising / Steady / Slipping / Declining / New — derived from QoQ momentum.',
                  },
                ]}
                bullets={[
                  'Hot — recent 3M growing > 25%',
                  'Concentrated — one account ≥ 40%',
                  'Regional — one state ≥ 50%',
                  'Dark — > 60 days since last activity',
                  'Dist risk — one distributor ≥ 50%',
                  'Bleeding — net account loss ≥ 5 in 90d',
                ]}
              />
            }
          >
            SKU
          </ColumnHeader>
          <ColumnHeader
            tooltip={
              <TooltipBlock
                title="12-month volume trend"
                bullets={[
                  'Bars run oldest → latest, left to right',
                  'Tallest bar = peak month',
                  'Outlined bar = current month',
                  'Rail bar = no depletions that month',
                ]}
                tip="Hover any bar for the exact month + 6-pk volume."
              />
            }
          >
            12-mo Trend
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Volume & share"
                items={[
                  {
                    label: 'Volume',
                    description:
                      'Total 6-pack equivalents over the selected date range (1 case = 2 six-packs); 9L equivalent on hover.',
                  },
                  {
                    label: 'Share',
                    description: "This SKU's portion of total portfolio volume in the same range.",
                  },
                ]}
                tip="Number is tinted by momentum: green if rising, red if declining."
              />
            }
          >
            Volume · Share
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Distribution shape"
                items={[
                  {
                    label: 'Reach',
                    description: 'Active accounts and states the SKU is sold in.',
                  },
                  {
                    label: 'Depth',
                    description:
                      'Avg 6-pk per account — high depth means each buyer takes more; low depth means broad but trickles.',
                  },
                  {
                    label: 'Net 90d',
                    description: 'Accounts gained vs churned in the last 3 months.',
                  },
                ]}
                tip="Depth is compared against the portfolio median for context."
              />
            }
          >
            Reach · Depth
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Concentration"
                items={[
                  {
                    label: 'Top state',
                    description: 'Largest state by 6-pk with its share of this SKU.',
                  },
                  {
                    label: 'Top account',
                    description:
                      'Sub-line shows the largest single-account share — amber flag when ≥ 40%.',
                  },
                ]}
                tip="Hover the cell to see the full top-3 states ranked by volume."
              />
            }
          >
            Top State
          </ColumnHeader>
          <ColumnHeader
            align="right"
            tooltip={
              <TooltipBlock
                title="Momentum"
                items={[
                  {
                    label: 'QoQ',
                    description:
                      'Recent 3M vs prior 3M — the headline chip. Falls back to absolute Δ on tiny baselines.',
                  },
                  {
                    label: 'YoY',
                    description:
                      'Recent 12M vs prior 12M — smooths the seasonal noise QoQ alone picks up.',
                  },
                ]}
                tip="When QoQ is red but YoY is flat, the recent dip is likely seasonal — not a real trend."
              />
            }
          >
            QoQ · YoY
          </ColumnHeader>
        </Box>

        {visible.map((p, i) => (
          <ProductRow
            key={p.product_id}
            index={i + 1}
            item={p}
            portfolioMedians={portfolioMedians}
          />
        ))}

        {visible.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>
            No SKUs match the current filter.
          </Box>
        )}
      </Box>

      {/* Reader's guide. */}
      <SectionExplainer
        rows={[
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: colors.success,
                  bgcolor: `${colors.success}14`,
                  border: `1px solid ${colors.success}40`,
                  px: 0.6,
                  py: 0.2,
                  borderRadius: 0.75,
                  whiteSpace: 'nowrap',
                }}
              >
                Hot
              </Box>
            ),
            term: 'SKU momentum chip (Hot · Growing · Steady · Slipping · Declining · New)',
            desc: (
              <>
                Compares the SKU's <strong>recent 3M</strong> 6-pk to the <strong>3M before</strong>
                . Sort or filter by momentum to find SKUs accelerating, cooling, or holding pace.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 8.5,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: colors.textMuted,
                  bgcolor: colors.surfaceHover,
                  border: `1px dashed ${colors.border}`,
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 0.5,
                  whiteSpace: 'nowrap',
                }}
              >
                LOW BASE
              </Box>
            ),
            term: 'LOW BASE marker',
            desc: (
              <>
                Fires when the prior 3M was very small. A <strong>+200%</strong> jump from 0.5 6-pk
                is not the same signal as <strong>+200%</strong> from 50 — the marker asks you to
                read the <strong>absolute</strong> change instead.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: 'uppercase',
                  color: colors.warning,
                  bgcolor: `${colors.warning}14`,
                  border: `1px solid ${colors.warning}40`,
                  px: 0.5,
                  py: 0.15,
                  borderRadius: 0.75,
                  whiteSpace: 'nowrap',
                }}
              >
                FLAG
              </Box>
            ),
            term: 'Flag chips next to the SKU name',
            desc: (
              <>
                Health signals derived from the row: <strong>Concentrated</strong> (one account
                &gt;30% of SKU), <strong>Regional</strong> (one state dominates),{' '}
                <strong>Dist risk</strong> (single distributor carries it), <strong>Dark</strong>{' '}
                (silent recently), <strong>Bleeding</strong> (steep recent drop).
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  fontFeatureSettings: '"tnum"',
                  bgcolor: colors.surfaceHover,
                  px: 0.6,
                  py: 0.15,
                  borderRadius: 0.75,
                }}
              >
                32.4%
              </Box>
            ),
            term: 'Share of portfolio (%)',
            desc: (
              <>
                What fraction of total 6-pk this SKU carries. A heavy share concentration on a
                couple of SKUs means a single supply or demand shock can move the whole brand.
              </>
            ),
          },
          {
            sample: (
              <Box
                component="span"
                sx={{
                  fontSize: 14,
                  color: colors.chartPrimary,
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                ⌄
              </Box>
            ),
            term: 'Click a row to expand',
            desc: (
              <>
                Opens a deep-dive panel: a <strong>year-over-year timeline</strong> (one line per
                year over the Jan–Dec axis, with year/month filters), the top-3 states the SKU sells
                in, top-3 accounts, top-3 distributors, and the QoQ + YoY momentum windows side by
                side.
              </>
            ),
          },
        ]}
      />
    </Paper>
  );
}

interface ProductRowProps {
  index: number;
  item: ProductPerformanceItem;
  portfolioMedians: { depth: number; accounts: number };
}

// Thresholds for the inline health flags. Calibrated against the
// current HY portfolio (top SKU has 23 states, 2k+ accounts; smallest
// has <5 states and <30 accounts).
// velocity_pct / yoy_pct arrive as 0..100; shares as 0..1.
const pctFraction = (v: number | null): number | null => (v === null ? null : v / 100);

function buildProductExport(
  rows: ProductPerformanceItem[],
  filters: ExportFilter[],
): ExcelWorkbookSpec {
  const monthly = rows.flatMap((p) =>
    (p.monthly_series ?? []).map((m) => ({ product: p.product_name, ...m })),
  );
  return {
    title: 'Product Performance',
    filters,
    sheets: [
      excelSheet({
        name: 'Products',
        rows,
        columns: [
          { header: 'Product', value: (p) => p.product_name, width: 34 },
          { header: '9L Cases', value: (p) => toNum(p.cases_9l), format: 'decimal' },
          { header: 'Physical Cases', value: (p) => toNum(p.cases_physical), format: 'decimal' },
          { header: 'Share of 9L', value: (p) => p.pct_of_9l, format: 'percent' },
          { header: 'Accounts', value: (p) => p.account_count, format: 'int' },
          { header: 'States', value: (p) => p.state_count, format: 'int' },
          {
            header: 'Avg 9L / Account',
            value: (p) => toNum(p.avg_9l_per_account),
            format: 'decimal',
          },
          { header: 'Top Account', value: (p) => p.top_account_name, width: 30 },
          { header: 'Top Account Share', value: (p) => p.top_account_share, format: 'percent' },
          { header: 'Top State', value: (p) => p.top_state, width: 9 },
          { header: 'Top State Share', value: (p) => p.top_state_share, format: 'percent' },
          { header: 'Top Distributor', value: (p) => p.top_distributor_code },
          {
            header: 'Top Distributor Share',
            value: (p) => p.top_distributor_share,
            format: 'percent',
          },
          { header: 'Distributors', value: (p) => p.distributor_count, format: 'int' },
          { header: 'Recent 3M 9L', value: (p) => toNum(p.recent_3m_9l), format: 'decimal' },
          { header: 'Prior 3M 9L', value: (p) => toNum(p.prior_3m_9l), format: 'decimal' },
          { header: 'QoQ Velocity', value: (p) => pctFraction(p.velocity_pct), format: 'percent' },
          { header: 'Momentum', value: (p) => p.momentum, width: 11 },
          { header: 'Recent 12M 9L', value: (p) => toNum(p.recent_12m_9l), format: 'decimal' },
          { header: 'Prior 12M 9L', value: (p) => toNum(p.prior_12m_9l), format: 'decimal' },
          { header: 'YoY', value: (p) => pctFraction(p.yoy_pct), format: 'percent' },
          { header: 'Accounts Gained 90d', value: (p) => p.accounts_gained_90d, format: 'int' },
          { header: 'Accounts Churned 90d', value: (p) => p.accounts_churned_90d, format: 'int' },
          { header: 'First Active', value: (p) => isoDate(p.first_active), format: 'date' },
          { header: 'Last Active', value: (p) => isoDate(p.last_active), format: 'date' },
          { header: 'Peak Month', value: (p) => isoDate(p.peak_month), format: 'date' },
          { header: 'Peak Month 9L', value: (p) => toNum(p.peak_month_9l), format: 'decimal' },
        ],
      }),
      ...(monthly.length > 0
        ? [
            excelSheet({
              name: 'Monthly by Product',
              rows: monthly,
              columns: [
                { header: 'Product', value: (r) => r.product, width: 34 },
                { header: 'Month', value: (r) => isoDate(r.period), format: 'date' },
                { header: '9L Cases', value: (r) => toNum(r.cases_9l), format: 'decimal' },
              ],
            }),
          ]
        : []),
    ],
  };
}

const FLAG_CONCENTRATED_THRESHOLD = 0.4; // top account >= 40% of SKU
const FLAG_REGIONAL_THRESHOLD = 0.5; // top state >= 50% of SKU
const FLAG_DARK_DAYS = 60; // last active > 60 days
const FLAG_DIST_RISK_THRESHOLD = 0.5; // top distributor >= 50% of SKU
const FLAG_NET_LOSS_THRESHOLD = 5; // net account loss >= 5 in last 3M

function ProductRow({ index, item, portfolioMedians }: ProductRowProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const [expanded, setExpanded] = useState(false);

  const volume = Number(item.cases_9l);
  const physical = Number(item.cases_physical);
  const avgPerAccount = Number(item.avg_9l_per_account);
  const sharePct = item.pct_of_9l * 100;
  const topStateSharePct = item.top_state_share * 100;
  const topAcctSharePct = item.top_account_share * 100;
  const topAcctRisky = topAcctSharePct >= 40;
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);
  const daysSinceLast = daysSinceIso(item.last_active);

  // Health flags — quick scannable signals that summarize the
  // strategic story without forcing the user to read every number.
  const flags: Array<{
    key: string;
    Icon: typeof TrendingUpIcon;
    label: string;
    color: string;
    bg: string;
    tooltip: string;
  }> = [];
  if (item.momentum === 'rising') {
    flags.push({
      key: 'hot',
      Icon: LocalFireDepartmentIcon,
      label: 'Hot',
      color: colors.success,
      bg: `${colors.success}14`,
      tooltip: `Rising — QoQ ${qoqChipText(item.velocity_pct, prior3m, recent3m)} vs prior 3 months.`,
    });
  }
  if (item.top_account_share >= FLAG_CONCENTRATED_THRESHOLD && item.top_account_name) {
    flags.push({
      key: 'conc',
      Icon: WarningAmberIcon,
      label: 'Concentrated',
      color: colors.warning,
      bg: `${colors.warning}14`,
      tooltip: `${item.top_account_name} (${item.top_account_share >= 0.01 ? `${topAcctSharePct.toFixed(0)}% of this SKU` : '—'}). One account dominates — losing them would hurt this SKU disproportionately.`,
    });
  }
  if (item.top_state_share >= FLAG_REGIONAL_THRESHOLD && item.top_state) {
    flags.push({
      key: 'regional',
      Icon: PinDropIcon,
      label: 'Regional',
      color: colors.info,
      bg: `${colors.info}14`,
      tooltip: `${item.top_state} carries ${topStateSharePct.toFixed(0)}% of this SKU. Expansion outside ${item.top_state} is the obvious growth lever.`,
    });
  }
  if (daysSinceLast !== null && daysSinceLast > FLAG_DARK_DAYS) {
    flags.push({
      key: 'dark',
      Icon: NightsStayIcon,
      label: 'Dark',
      color: colors.error,
      bg: `${colors.error}14`,
      tooltip: `No depletion activity for ${daysSinceLast} days. Last active ${
        item.last_active ? shortMonth(item.last_active) : '—'
      }.`,
    });
  }
  if (item.top_distributor_share >= FLAG_DIST_RISK_THRESHOLD && item.top_distributor_code) {
    flags.push({
      key: 'dist',
      Icon: WarehouseIcon,
      label: 'Dist risk',
      color: colors.warning,
      bg: `${colors.warning}14`,
      tooltip: `${item.top_distributor_code} carries ${Math.round(item.top_distributor_share * 100)}% of this SKU across ${item.distributor_count} distributors. Losing the relationship would hit this SKU hard.`,
    });
  }
  const net90d = item.accounts_gained_90d - item.accounts_churned_90d;
  if (net90d <= -FLAG_NET_LOSS_THRESHOLD) {
    flags.push({
      key: 'bleeding',
      Icon: TrendingDownIcon,
      label: 'Bleeding',
      color: colors.error,
      bg: `${colors.error}14`,
      tooltip: `Net account loss of ${Math.abs(net90d)} in the last 3 months (${item.accounts_gained_90d} gained, ${item.accounts_churned_90d} churned). Volume may follow.`,
    });
  }

  // Depth vs portfolio median — context for the "X 9L/acct" line.
  const depthPctVsMedian =
    portfolioMedians.depth > 0
      ? ((avgPerAccount - portfolioMedians.depth) / portfolioMedians.depth) * 100
      : 0;
  const depthSignal: { label: string; color: string } = (() => {
    if (avgPerAccount === 0) return { label: 'no depth', color: colors.textMuted };
    if (depthPctVsMedian >= 25) return { label: 'deeper than median', color: colors.success };
    if (depthPctVsMedian <= -25) return { label: 'thinner than median', color: colors.warning };
    return { label: 'around median', color: colors.textMuted };
  })();

  // Volume number is tinted very subtly by momentum so the eye groups
  // rising SKUs without each row needing to be read.
  const volumeColor =
    item.momentum === 'rising'
      ? colors.success
      : item.momentum === 'declining'
        ? colors.error
        : colors.textPrimary;
  // Very faint row background by momentum — barely visible, only
  // amplifies the eye's grouping.
  const rowTint =
    item.momentum === 'rising'
      ? `${colors.success}06`
      : item.momentum === 'declining'
        ? `${colors.error}06`
        : 'transparent';

  return (
    <Box
      sx={{
        bgcolor: expanded ? colors.surfaceSubtle : rowTint,
        borderBottom: `1px solid ${colors.borderLight}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((x) => !x);
          }
        }}
        sx={{
          display: 'grid',
          gridTemplateColumns: '22px 32px 2fr 152px 1.3fr 1.2fr 1.2fr 1fr',
          gap: 1,
          px: 1.75,
          py: 1.25,
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'background-color 0.12s',
          '&:hover': { bgcolor: colors.surfaceHover },
          '&:focus-visible': {
            outline: `2px solid ${colors.chartPrimary}`,
            outlineOffset: -2,
          },
        }}
      >
        {/* Expand chevron */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ChevronRightIcon
            sx={{
              fontSize: 16,
              color: colors.textMuted,
              transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s ease',
            }}
          />
        </Box>

        {/* Rank with momentum left-bar accent */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 3,
              alignSelf: 'stretch',
              bgcolor: presentation.color,
              borderRadius: 1,
              opacity: 0.7,
              mr: -0.25,
            }}
          />
          <Typography
            sx={{
              fontSize: 11.5,
              fontWeight: 700,
              color: colors.textMuted,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {index}
          </Typography>
        </Box>

        {/* SKU name + momentum badge + health flags */}
        <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip
            placement="top"
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                  {item.product_name}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto auto',
                    gap: '4px 12px',
                    fontSize: 11,
                  }}
                >
                  <span style={{ opacity: 0.7 }}>Recent 3M</span>
                  <span style={{ fontWeight: 600 }}>{fmt6PkNumber(recent3m)} 6-pk</span>
                  <span style={{ opacity: 0.7 }}>Prior 3M</span>
                  <span style={{ fontWeight: 600 }}>{fmt6PkNumber(prior3m)} 6-pk</span>
                  <span style={{ opacity: 0.7 }}>First active</span>
                  <span style={{ fontWeight: 600 }}>
                    {item.first_active ? shortMonth(item.first_active) : '—'}
                  </span>
                  <span style={{ opacity: 0.7 }}>Last active</span>
                  <span style={{ fontWeight: 600 }}>
                    {item.last_active ? shortMonth(item.last_active) : '—'}
                  </span>
                </Box>
                <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
                  Click row for full deep-dive
                </Typography>
              </Box>
            }
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'help',
                borderBottom: `1px dotted ${colors.borderLight}`,
                display: 'inline-block',
                maxWidth: '100%',
              }}
            >
              {shortSkuName(item.product_name)}
            </Typography>
          </Tooltip>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.4,
              flexWrap: 'wrap',
            }}
          >
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                color: presentation.color,
                px: 0.6,
                py: 0.1,
                borderRadius: 0.75,
                bgcolor: presentation.bg,
              }}
            >
              {presentation.label}
            </Box>
            {flags.map((f) => (
              <FlagChip
                key={f.key}
                Icon={f.Icon}
                label={f.label}
                color={f.color}
                bg={f.bg}
                tooltip={f.tooltip}
              />
            ))}
          </Box>
        </Box>

        {/* Sparkline — tooltip now shows all 12 months */}
        <Tooltip
          placement="top"
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                12-month volume
              </Typography>
              {item.monthly_series.map((s) => (
                <Box
                  key={s.period}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    fontFeatureSettings: '"tnum"',
                  }}
                >
                  <span style={{ opacity: 0.7 }}>{shortMonth(s.period)}</span>
                  <span style={{ fontWeight: 600 }}>{fmt6PkNumber(s.cases_9l)} 6-pk</span>
                </Box>
              ))}
            </Box>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
            <Sparkline series={item.monthly_series} color={presentation.color} />
          </Box>
        </Tooltip>

        {/* Volume + share — number tinted by momentum */}
        <Tooltip
          placement="top"
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                Volume breakdown
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
                <span style={{ opacity: 0.7 }}>6-pack equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>9L equivalents</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {num.format(volume)}
                </span>
                <span style={{ opacity: 0.7 }}>Physical cases</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {physical > 0 ? num.format(physical) : '—'}
                </span>
                <span style={{ opacity: 0.7 }}>Share of portfolio</span>
                <span style={{ fontWeight: 600 }}>{sharePct.toFixed(1)}%</span>
              </Box>
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: volumeColor,
                lineHeight: 1,
                fontFeatureSettings: '"tnum","lnum"',
              }}
            >
              {fmt6PkNumber(volume)}
            </Typography>
            <Typography sx={{ fontSize: 10.5, color: colors.textMuted, mt: 0.25 }}>
              6-pk · {num.format(volume)} 9L · {sharePct.toFixed(1)}% share
            </Typography>
          </Box>
        </Tooltip>

        {/* Reach + depth with portfolio-median context + net 90d acct momentum */}
        <Tooltip
          placement="top"
          title={
            <Box sx={{ p: 0.5, fontSize: 11 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.5 }}>
                Reach &amp; depth
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '4px 12px' }}>
                <span style={{ opacity: 0.7 }}>Active accounts (total)</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {numInt.format(item.account_count)}
                </span>
                <span style={{ opacity: 0.7 }}>Active in last 3M</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {numInt.format(item.accounts_active_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>Gained in last 3M</span>
                <span
                  style={{
                    fontWeight: 600,
                    fontFeatureSettings: '"tnum"',
                    color: colors.success,
                  }}
                >
                  +{numInt.format(item.accounts_gained_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>Churned in last 3M</span>
                <span
                  style={{
                    fontWeight: 600,
                    fontFeatureSettings: '"tnum"',
                    color: colors.error,
                  }}
                >
                  −{numInt.format(item.accounts_churned_90d)}
                </span>
                <span style={{ opacity: 0.7 }}>States this Q / total</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {item.states_active_recent_q} / {item.state_count}
                </span>
                <span style={{ opacity: 0.7 }}>Avg 6-pk per acct</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {num.format(avgPerAccount)}
                </span>
                <span style={{ opacity: 0.7 }}>Portfolio median</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {fmt6PkNumber(portfolioMedians.depth)} 6-pk/acct
                </span>
              </Box>
              <Typography sx={{ fontSize: 10.5, mt: 0.75, opacity: 0.85, fontStyle: 'italic' }}>
                Net 90d = gained − churned. Negative means the SKU is losing buyers faster than it
                wins them. Depth = volume ÷ accounts.
              </Typography>
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textPrimary,
                lineHeight: 1,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {numInt.format(item.account_count)}
              <Box
                component="span"
                sx={{ color: colors.textMuted, fontWeight: 400, ml: 0.5, fontSize: 11 }}
              >
                accts · {item.state_count} st
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: 10.5,
                color: depthSignal.color,
                mt: 0.25,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {fmt6PkNumber(avgPerAccount)} 6-pk/acct · {depthSignal.label}
            </Typography>
            <NetAccountsLine
              gained={item.accounts_gained_90d}
              churned={item.accounts_churned_90d}
            />
          </Box>
        </Tooltip>

        {/* Top state with top-3 list tooltip */}
        <Tooltip
          placement="top"
          title={
            <Box sx={{ p: 0.5, fontSize: 11, minWidth: 200 }}>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, mb: 0.75 }}>
                Top states for this SKU
              </Typography>
              {item.top_states.length === 0 ? (
                <Typography sx={{ fontSize: 11, opacity: 0.7 }}>No state data</Typography>
              ) : (
                item.top_states.map((s) => (
                  <Box
                    key={s.state_code ?? '∅'}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '32px 1fr auto',
                      gap: 0.75,
                      alignItems: 'baseline',
                      mb: 0.4,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    <span style={{ fontWeight: 700 }}>{s.state_code ?? '—'}</span>
                    <span style={{ opacity: 0.7 }}>
                      {fmt6PkNumber(s.cases_9l)} 6-pk · {s.account_count} accts
                    </span>
                    <span style={{ fontWeight: 600 }}>{(s.share * 100).toFixed(0)}%</span>
                  </Box>
                ))
              )}
            </Box>
          }
        >
          <Box sx={{ textAlign: 'right', cursor: 'help' }}>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: colors.textPrimary,
                lineHeight: 1,
                fontFeatureSettings: '"tnum"',
              }}
            >
              {item.top_state ?? '—'}
              <Box
                component="span"
                sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5, fontSize: 11 }}
              >
                {topStateSharePct.toFixed(0)}%
              </Box>
            </Typography>
            {item.top_account_name && (
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: topAcctRisky ? colors.warning : colors.textMuted,
                  mt: 0.25,
                  fontFeatureSettings: '"tnum"',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {topAcctRisky ? '⚠ ' : ''}Top acct {topAcctSharePct.toFixed(0)}%
              </Typography>
            )}
          </Box>
        </Tooltip>

        {/* QoQ + YoY chips stacked — QoQ is the headline, YoY sits
            below so the user can see if the recent dip is a real trend
            or a seasonal blip. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.4,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <MomentumChip
            momentum={item.momentum}
            velocityPct={item.velocity_pct}
            recent3m={recent3m}
            prior3m={prior3m}
          />
          <YoyChip
            velocityPct={item.yoy_pct}
            recent12m={Number(item.recent_12m_9l)}
            prior12m={Number(item.prior_12m_9l)}
          />
        </Box>
      </Box>

      {/* Expand panel — deep dive */}
      {expanded && <ExpandedDeepDive item={item} portfolioMedians={portfolioMedians} />}
    </Box>
  );
}

// Deep-dive panel shown below the main row when expanded. Layout:
// A full-width monthly timeline on top, then Top-3 accounts + states +
// distributors, with a momentum block surfacing recent vs prior 3M.
interface ExpandedDeepDiveProps {
  item: ProductPerformanceItem;
  portfolioMedians: { depth: number; accounts: number };
}

function ExpandedDeepDive({ item, portfolioMedians }: ExpandedDeepDiveProps) {
  const presentation = MOMENTUM_PRESENTATION[item.momentum];
  const recent3m = Number(item.recent_3m_9l);
  const prior3m = Number(item.prior_3m_9l);
  const maxBaselineWidth = Math.max(recent3m, prior3m, 1);
  const physical = Number(item.cases_physical);
  const volume = Number(item.cases_9l);
  const avgPerAccount = Number(item.avg_9l_per_account);
  const daysSinceLast = daysSinceIso(item.last_active);

  // Seasonality label — simple "Peaks in <Mon>" derived from the peak
  // month. We only have ~24 months of data so a richer multi-year
  // seasonality model isn't worth building yet.
  const peakMonthLabel = item.peak_month ? shortMonth(item.peak_month) : null;
  const peakMonthName = item.peak_month
    ? (['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
        Number(item.peak_month.split('-')[1]) - 1
      ] ?? null)
    : null;
  const net90d = item.accounts_gained_90d - item.accounts_churned_90d;
  // Activity rhythm one-liner.
  const rhythmLabel =
    item.months_active_12m === 12
      ? 'Active every month'
      : item.months_active_12m >= 9
        ? 'Mostly active'
        : item.months_active_12m >= 5
          ? 'Sporadic'
          : 'Rare';
  const recent12m = Number(item.recent_12m_9l);
  const prior12m = Number(item.prior_12m_9l);

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderTop: `1px dashed ${colors.borderLight}`,
        bgcolor: colors.surfaceSubtle,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1.4fr' },
        gap: 2.5,
      }}
    >
      {/* Full-width year-over-year timeline for this SKU — same chart as
          the Monthly Depletion Volume section, scoped to this product's
          full history, with the year toggles + month-window filter. */}
      <Box sx={{ gridColumn: '1 / -1' }}>
        <DepletionsTrendChart
          productId={item.product_id}
          title={`${item.product_name} — monthly timeline`}
          subtitle="Full history · 6-pk volume by month, one line per year — toggle years or narrow the month window"
          compact
          exportable={false}
        />
      </Box>

      {/* Top 3 accounts */}
      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            mb: 1,
          }}
        >
          Top accounts
        </Typography>
        {item.top_accounts.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
            No account data for this window.
          </Typography>
        ) : (
          item.top_accounts.map((a) => (
            <Box key={a.account_id} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.textPrimary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={a.name}
                  >
                    {a.name}
                  </Typography>
                  <PremisesBadge premises={a.premises_type} online={a.is_online_retailer} />
                  <Box
                    component="span"
                    sx={{ fontSize: 10.5, color: colors.textMuted, fontWeight: 500 }}
                  >
                    {a.state_code ?? ''}
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(a.cases_9l)} 6-pk · {num.format(Number(a.cases_9l))} 9L
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(a.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={a.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Top 3 states */}
      <Box>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            mb: 1,
          }}
        >
          Top states
        </Typography>
        {item.top_states.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
            No state data for this window.
          </Typography>
        ) : (
          item.top_states.map((s) => (
            <Box key={s.state_code ?? '∅'} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
                  {s.state_code ?? '—'}
                  <Box
                    component="span"
                    sx={{ fontSize: 10.5, color: colors.textMuted, fontWeight: 500, ml: 0.5 }}
                  >
                    {s.account_count} accts
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(Number(s.cases_9l))} 6-pk
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(s.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={s.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Top 3 distributors — strategic risk view. Single distributor
          concentration is more dangerous than single account: losing
          the wholesaler relationship affects every outlet downstream. */}
      <Box>
        <Box
          sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1 }}
        >
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
            }}
          >
            Top distributors
          </Typography>
          {item.distributor_count > 0 && (
            <Typography sx={{ fontSize: 10, color: colors.textMuted, fontWeight: 500 }}>
              {item.distributor_count} total
            </Typography>
          )}
        </Box>
        {item.top_distributors.length === 0 ? (
          <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
            No distributor data for this window.
          </Typography>
        ) : (
          item.top_distributors.map((d) => (
            <Box key={d.distributor_code ?? '∅'} sx={{ mb: 1.25 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  gap: 1,
                  mb: 0.3,
                }}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: colors.textPrimary }}>
                  {d.distributor_code ?? '—'}
                  <Box
                    component="span"
                    sx={{ fontSize: 10.5, color: colors.textMuted, fontWeight: 500, ml: 0.5 }}
                  >
                    {d.account_count} accts
                  </Box>
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: colors.textSecondary,
                    fontFeatureSettings: '"tnum"',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt6PkNumber(Number(d.cases_9l))} 6-pk
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    {(d.share * 100).toFixed(1)}%
                  </Box>
                </Typography>
              </Box>
              <MiniBar share={d.share} color={presentation.color} />
            </Box>
          ))
        )}
      </Box>

      {/* Trajectory + lifecycle metadata */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Recent vs Prior comparison */}
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 1,
            }}
          >
            Recent vs Prior 3M
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Recent
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={recent3m / maxBaselineWidth} color={presentation.color} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(recent3m)} 6-pk
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                Prior
              </Box>
              <Box sx={{ flex: 1 }}>
                <MiniBar share={prior3m / maxBaselineWidth} color={colors.textMuted} />
              </Box>
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  color: colors.textSecondary,
                  fontFeatureSettings: '"tnum"',
                  minWidth: 60,
                  textAlign: 'right',
                }}
              >
                {fmt6PkNumber(prior3m)} 6-pk
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Year-over-year comparison bars — smooths seasonal noise
            that QoQ alone picks up. Recent 12M vs prior 12M. */}
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 1,
            }}
          >
            Recent vs Prior 12M (YoY)
          </Typography>
          {(() => {
            const maxYoy = Math.max(recent12m, prior12m, 1);
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Recent
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={recent12m / maxYoy} color={presentation.color} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(recent12m)} 6-pk
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Box sx={{ width: 44, fontSize: 10.5, color: colors.textMuted, fontWeight: 600 }}>
                    Prior
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <MiniBar share={prior12m / maxYoy} color={colors.textMuted} />
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      color: colors.textSecondary,
                      fontFeatureSettings: '"tnum"',
                      minWidth: 70,
                      textAlign: 'right',
                    }}
                  >
                    {fmt6PkNumber(prior12m)} 6-pk
                  </Box>
                </Box>
              </Box>
            );
          })()}
        </Box>

        {/* Activity rhythm + account momentum + states this Q + lifecycle */}
        <Box>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 0.75,
            }}
          >
            SKU facts
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '4px 12px',
              fontSize: 11.5,
              alignItems: 'baseline',
            }}
          >
            <span style={{ color: colors.textMuted }}>Active rhythm</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {item.months_active_12m} / 12 months
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · {rhythmLabel}
              </Box>
            </span>
            {peakMonthName && (
              <>
                <span style={{ color: colors.textMuted }}>Peak month</span>
                <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
                  {peakMonthLabel}
                  <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                    · {fmt6PkNumber(Number(item.peak_month_9l))} 6-pk · Peaks in {peakMonthName}
                  </Box>
                </span>
              </>
            )}
            <span style={{ color: colors.textMuted }}>States this Q</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {item.states_active_recent_q} of {item.state_count}
              {item.state_count > 0 && (
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  ·{' '}
                  {Math.round((item.states_active_recent_q / Math.max(item.state_count, 1)) * 100)}%
                  footprint
                </Box>
              )}
            </span>
            <span style={{ color: colors.textMuted }}>Acct momentum 90d</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              <Box component="span" sx={{ color: colors.success, fontWeight: 700 }}>
                +{item.accounts_gained_90d}
              </Box>
              {' / '}
              <Box component="span" sx={{ color: colors.error, fontWeight: 700 }}>
                −{item.accounts_churned_90d}
              </Box>
              <Box
                component="span"
                sx={{
                  color: net90d > 0 ? colors.success : net90d < 0 ? colors.error : colors.textMuted,
                  fontWeight: 700,
                  ml: 0.5,
                }}
              >
                ({net90d > 0 ? '+' : ''}
                {net90d} net)
              </Box>
            </span>
            <span style={{ color: colors.textMuted }}>Total volume</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(volume)} 6-pk
              {physical > 0 && (
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  · {num.format(physical)} physical
                </Box>
              )}
            </span>
            <span style={{ color: colors.textMuted }}>Avg / account</span>
            <span style={{ fontWeight: 600, fontFeatureSettings: '"tnum"' }}>
              {fmt6PkNumber(avgPerAccount)} 6-pk
              <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                · median {num.format(portfolioMedians.depth)}
              </Box>
            </span>
            <span style={{ color: colors.textMuted }}>Lifecycle</span>
            <span style={{ fontWeight: 600 }}>
              {item.first_active ? shortMonth(item.first_active) : '—'} →{' '}
              {item.last_active ? shortMonth(item.last_active) : '—'}
              {daysSinceLast !== null && (
                <Box component="span" sx={{ color: colors.textMuted, fontWeight: 500, ml: 0.5 }}>
                  · {daysSinceLast}d ago
                </Box>
              )}
            </span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface ToolbarMenuProps {
  label: string;
  value: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function ToolbarMenu({ label, value, open, onToggle, children }: ToolbarMenuProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, onToggle, open);
  return (
    <Box ref={wrapperRef} sx={{ position: 'relative' }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.6,
          borderRadius: 1,
          border: `1px solid ${open ? colors.chartPrimary : colors.border}`,
          bgcolor: open ? `${colors.chartPrimary}08` : '#fff',
          fontSize: 12,
          fontWeight: 500,
          color: colors.textPrimary,
          transition: 'border-color 0.12s, background-color 0.12s',
          '&:hover': { borderColor: colors.chartPrimary },
        }}
      >
        <Box component="span" sx={{ color: colors.textMuted, fontSize: 11 }}>
          {label}:
        </Box>
        <Box component="span" sx={{ fontWeight: 600 }}>
          {value}
        </Box>
        {open ? (
          <KeyboardArrowUpIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        ) : (
          <KeyboardArrowDownIcon sx={{ fontSize: 14, color: colors.textMuted }} />
        )}
      </Box>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 10,
            minWidth: 220,
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            boxShadow: '0 12px 28px rgba(0,0,0,0.10), 0 2px 6px rgba(0,0,0,0.05)',
            overflow: 'hidden',
            py: 0.5,
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}

interface MenuRowProps {
  selected: boolean;
  primary: string;
  secondary?: string;
  onClick: () => void;
}

function MenuRow({ selected, primary, secondary, onClick }: MenuRowProps) {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        all: 'unset',
        cursor: 'pointer',
        display: 'block',
        width: '100%',
        px: 1.5,
        py: 0.9,
        textAlign: 'left',
        bgcolor: selected ? `${colors.chartPrimary}10` : 'transparent',
        '&:hover': { bgcolor: selected ? `${colors.chartPrimary}18` : colors.surfaceHover },
      }}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: selected ? 700 : 500,
          color: selected ? colors.chartPrimary : colors.textPrimary,
          lineHeight: 1.3,
        }}
      >
        {primary}
      </Typography>
      {secondary && (
        <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.15 }}>
          {secondary}
        </Typography>
      )}
    </Box>
  );
}
